// Alerta sonoro de notificação via Web Audio (sem arquivo de áudio). Toca um
// padrão de 3 toques bem audível. Browsers exigem um gesto do usuário antes de
// liberar áudio — por isso expomos `primeAudio()` pra "destravar" no 1º clique.
let ctx: AudioContext | null = null;

function ensureCtx(): AudioContext | null {
  try {
    const AC =
      window.AudioContext ||
      (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    if (!AC) return null;
    ctx = ctx || new AC();
    if (ctx.state === 'suspended') void ctx.resume();
    return ctx;
  } catch {
    return null;
  }
}

// Destrava o áudio no primeiro gesto (chamado uma vez no provider).
export function primeAudio() {
  ensureCtx();
}

// Toca uma sequência de notas. `type` muda o timbre (triangle = alerta metálico,
// sine = suave), `peak` o volume. Usado pelos dois sons abaixo.
function playTones(
  notes: { f: number; t: number }[],
  { type, peak, dur }: { type: OscillatorType; peak: number; dur: number }
) {
  const c = ensureCtx();
  if (!c) return;
  const now = c.currentTime;
  notes.forEach(({ f, t }) => {
    const osc = c.createOscillator();
    const gain = c.createGain();
    osc.type = type;
    osc.frequency.value = f;
    const start = now + t;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(peak, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + dur);
    osc.connect(gain).connect(c.destination);
    osc.start(start);
    osc.stop(start + dur + 0.02);
  });
}

// ALERTA de notificação (nova conversa / pronto pra atendimento): 3 toques
// ascendentes, timbre metálico (triangle), volume alto. Chama a atenção.
export function playNotificationSound() {
  playTones(
    [
      { f: 880, t: 0 },
      { f: 1108, t: 0.14 },
      { f: 1318, t: 0.28 },
    ],
    { type: 'triangle', peak: 0.6, dur: 0.22 }
  );
}

// ALERTA INSISTENTE de conversa nova.
//
// Um toque só passa despercebido: o atendente pode estar em outra aba, de
// costas, ou no meio de outra conversa — e cliente esperando é o que dói. Toca
// uma sequência mais longa e repete algumas vezes, com intervalo. Devolve uma
// função para cortar assim que ele reagir.
const REPETICOES = 5;
const INTERVALO_MS = 2600;

// Sequência maior e mais aguda que o toque normal: sobe, cai e sobe de novo,
// que o ouvido lê como urgência em vez de "chegou algo".
export function playToqueUrgente() {
  playTones(
    [
      { f: 988, t: 0 },
      { f: 1318, t: 0.12 },
      { f: 988, t: 0.24 },
      { f: 1318, t: 0.36 },
      { f: 1568, t: 0.5 },
    ],
    { type: 'triangle', peak: 0.75, dur: 0.2 }
  );
}

export function startInsistentAlert(): () => void {
  playToqueUrgente();
  let restantes = REPETICOES - 1;
  const timer = window.setInterval(() => {
    if (restantes <= 0) {
      window.clearInterval(timer);
      return;
    }
    restantes -= 1;
    playToqueUrgente();
  }, INTERVALO_MS);
  return () => window.clearInterval(timer);
}

// MENSAGEM nova numa conversa (só quando o atendente já está na aba de Suporte):
// 2 toques curtos e suaves (sine), volume menor. Deliberadamente DIFERENTE do
// alerta acima — timbre redondo em vez de metálico — pra distinguir de ouvido.
export function playMessageSound() {
  playTones(
    [
      { f: 587, t: 0 }, // ré
      { f: 784, t: 0.1 }, // sol
    ],
    { type: 'sine', peak: 0.32, dur: 0.14 }
  );
}
